import { ROOT_ID } from '../constants';

const buildChildIdsByParent = (items) =>
  items.reduce((acc, node) => {
    (acc[node.parent] = acc[node.parent] || []).push(node.id);
    return acc;
  }, {});

// A folder's own `selectedItemsMap` flag is unreliable for "is anything selected
// here" — its checkbox is derived from its pages — so selection is detected by
// walking down to the leaves.
const createHasSelection = (childIdsByParent, selectedItemsMap) => {
  const cache = new Map();

  const hasSelection = (id) => {
    if (cache.has(id)) return cache.get(id);

    const childIds = childIdsByParent[id];
    const result = childIds ? childIds.some(hasSelection) : Boolean(selectedItemsMap[id]);

    cache.set(id, result);
    return result;
  };

  return hasSelection;
};

// True when the node itself, or any item in its subtree, is selected.
export const isNodeInSelection = (nodeId, { selectedItemsMap = {}, items = [] }) => {
  const childIdsByParent = buildChildIdsByParent(items);
  const hasSelection = createHasSelection(childIdsByParent, selectedItemsMap);
  return hasSelection(nodeId);
};

// Resolves which items a drag gesture should move.
//
// Outside of select mode (or when the grabbed row has no selection in or under it)
// a drag moves just the grabbed item. When the grabbed row participates in the
// selection, the whole selection moves, collapsed to its roots: a folder is moved
// as a single root (carrying its subtree) only when it and every descendant are
// selected; a partially-selected folder is left in place and only its selected
// pages move, so deselected inner items never travel along.
const getDragItemIds = (dragSourceId, { selectModeEnabled, selectedItemsMap = {}, items = [] }) => {
  const childIdsByParent = buildChildIdsByParent(items);
  const hasSelection = createHasSelection(childIdsByParent, selectedItemsMap);

  if (!selectModeEnabled || !hasSelection(dragSourceId)) {
    return [dragSourceId];
  }

  const fullySelectedCache = new Map();
  const isFullySelected = (id) => {
    if (fullySelectedCache.has(id)) return fullySelectedCache.get(id);
    if (!selectedItemsMap[id]) {
      fullySelectedCache.set(id, false);
      return false;
    }

    const childIds = childIdsByParent[id] || [];
    const result = childIds.every(isFullySelected);
    fullySelectedCache.set(id, result);
    return result;
  };

  // A node is a move-root when it (and its whole subtree) is selected, but its
  // parent is not — so the parent isn't already carrying it.
  return items
    .filter((node) => {
      if (!isFullySelected(node.id)) return false;
      return node.parent === ROOT_ID || !isFullySelected(node.parent);
    })
    .map((node) => node.id);
};

// How many items the drag preview badge should announce: 1 for a plain single-item
// drag, otherwise the number of selected pages travelling with the selection (which
// matches the "N selected" counter, e.g. a fully selected folder counts its pages,
// not the single folder root the move collapses to).
export const getDraggedItemsCount = (
  dragSourceId,
  { selectModeEnabled, selectedItemsMap = {}, items = [] }
) => {
  const childIdsByParent = buildChildIdsByParent(items);
  const hasSelection = createHasSelection(childIdsByParent, selectedItemsMap);

  if (!selectModeEnabled || !hasSelection(dragSourceId)) {
    return 1;
  }

  return items.filter((node) => !node.droppable && selectedItemsMap[node.id]).length;
};

export default getDragItemIds;
